import React, { useState, useEffect } from 'react';
import { unduhPdf } from './unduhPdf';

const API_URL = 'https://skortes.com';

const MODUL_LABEL = {
  kraepelin: { nama: 'Kraepelin / Pauli', icon: '🧮' },
  kecermatan: { nama: 'Tes Kecermatan', icon: '✅' },
  logika: { nama: 'Logika & Numerik', icon: '🔢' },
  verbal: { nama: 'Verbal', icon: '📖' },
  figural: { nama: 'Figural & Spasial', icon: '🧩' },
  kepribadian: { nama: 'Kepribadian', icon: '🧠' },
  akademik: { nama: 'Kemampuan Akademik', icon: '📚' },
  armyalpha: { nama: 'Instruksi Cepat', icon: '⚡' },
};
const URUTAN_MODUL = Object.keys(MODUL_LABEL);

function formatTgl(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][d.getMonth()]} ${d.getFullYear()}`;
}
function formatRupiah(n) { return 'Rp' + Number(n || 0).toLocaleString('id-ID'); }

// ─── Grafik garis penuh (bukan sparkline) untuk 1 modul ─────────
function GrafikModulPenuh({ modulId, data }) {
  const W = 320, H = 90, PAD = 20;
  const skorList = data.map(d => d.skor);
  const maks = Math.max(100, ...skorList);
  const points = data.map((d, i) => {
    const x = PAD + (i / Math.max(1, data.length - 1)) * (W - PAD * 2);
    const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const info = MODUL_LABEL[modulId] || { nama: modulId, icon: '📝' };
  const terakhir = data[data.length - 1]?.skor;
  const rataModul = Math.round(skorList.reduce((a, b) => a + b, 0) / skorList.length);

  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{info.icon} {info.nama}</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>rata² <span className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{rataModul}</span></p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 70 }}>
        <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke="var(--border)" strokeWidth="1" />
        <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const x = PAD + (i / Math.max(1, data.length - 1)) * (W - PAD * 2);
          const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
          return <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="2.5" fill="var(--accent)" />;
        })}
      </svg>
      <div className="flex justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>{formatTgl(data[0]?.tanggal)}</span>
        <span>{data.length} sesi</span>
        <span>{formatTgl(data[data.length - 1]?.tanggal)}</span>
      </div>
      {terakhir != null && (
        <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Skor terakhir: <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{terakhir}</span></p>
      )}
    </div>
  );
}

// ─── Grafik radar 8 modul (kekuatan vs kelemahan) ───────────────
function GrafikRadar({ perModul }) {
  const N = URUTAN_MODUL.length;
  const W = 280, H = 280, CX = W / 2, CY = H / 2, R = 100;

  const nilai = URUTAN_MODUL.map(m => {
    const list = perModul[m];
    if (!list || list.length === 0) return 0;
    return Math.round(list.reduce((a, b) => a + b.skor, 0) / list.length);
  });

  const titik = (i, val) => {
    const angle = (Math.PI * 2 * i) / N - Math.PI / 2;
    const r = (val / 100) * R;
    return [CX + r * Math.cos(angle), CY + r * Math.sin(angle)];
  };
  const titikLabel = (i) => {
    const angle = (Math.PI * 2 * i) / N - Math.PI / 2;
    return [CX + (R + 24) * Math.cos(angle), CY + (R + 24) * Math.sin(angle)];
  };

  const poligonPoints = nilai.map((v, i) => titik(i, v).join(',')).join(' ');
  const cincin = [25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 280 }}>
      {cincin.map(c => (
        <polygon key={c}
          points={URUTAN_MODUL.map((_, i) => titik(i, c).join(',')).join(' ')}
          fill="none" stroke="var(--border)" strokeWidth="1" />
      ))}
      {URUTAN_MODUL.map((_, i) => {
        const [x, y] = titik(i, 100);
        return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="var(--border)" strokeWidth="1" />;
      })}
      <polygon points={poligonPoints} fill="rgba(192,160,98,0.25)" stroke="var(--accent)" strokeWidth="2" />
      {nilai.map((v, i) => {
        const [x, y] = titik(i, v);
        return <circle key={i} cx={x} cy={y} r="3" fill="var(--accent)" />;
      })}
      {URUTAN_MODUL.map((m, i) => {
        const [x, y] = titikLabel(i);
        return (
          <text key={m} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="var(--text-muted)">
            {MODUL_LABEL[m].icon}
          </text>
        );
      })}
    </svg>
  );
}

export default function ProfilLengkapUser({ userId, token, onTutup }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/admin/users/${userId}/profil-lengkap`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId, token]);

  const handleUnduhPdf = async () => {
    setLoadingPdf(true);
    try {
      await unduhPdf(`${API_URL}/api/admin/laporan/harian/${userId}`, token, `laporan-latihan-${data?.user?.nama || userId}.pdf`);
    } catch (err) {
      alert(err.message || 'Gagal mengunduh laporan.');
    }
    setLoadingPdf(false);
  };

  if (loading) {
    return <p className="text-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>Memuat profil lengkap...</p>;
  }
  if (!data || !data.user) {
    return (
      <div className="space-y-4">
        <button onClick={onTutup} className="text-sm" style={{ color: 'var(--accent)' }}>← Kembali</button>
        <p className="text-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>Gagal memuat data user.</p>
      </div>
    );
  }

  const { user, gamifikasi, aktivitas, tryout, pembayaran, notifikasi } = data;
  const badgeTercapai = gamifikasi.badges.filter(b => b.tercapai);
  const modulDenganData = URUTAN_MODUL.filter(m => aktivitas.perModul[m]?.length >= 2);

  return (
    <div className="space-y-5">
      <button onClick={onTutup} className="text-sm" style={{ color: 'var(--accent)' }}>← Kembali ke daftar</button>

      {/* Header identitas */}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0"
               style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)', border: '2px solid var(--accent)' }}>
            {user.nama.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>{user.nama}</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: user.jalur === 'bintara' ? 'rgba(251,191,36,0.15)' : 'rgba(74,222,128,0.1)', color: user.jalur === 'bintara' ? '#FBBF24' : 'var(--success)' }}>
                {user.jalur || 'belum pilih jalur'}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-card3)', color: user.role === 'admin' ? 'var(--success)' : 'var(--text-muted)' }}>
                {user.role}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: user.status === 'aktif' ? 'rgba(74,222,128,0.1)' : 'rgba(250,204,21,0.15)', color: user.status === 'aktif' ? 'var(--success)' : '#facc15' }}>
                {user.status}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-muted)' }}>
                {notifikasi.subscribe ? `🔔 ${notifikasi.jumlahDevice} device` : '🔕 belum aktifkan notif'}
              </span>
            </div>
            <p className="text-[11px] mt-2" style={{ color: 'var(--text-faint)' }}>📅 Bergabung {formatTgl(user.created_at)}</p>
          </div>
        </div>
        <button onClick={handleUnduhPdf} disabled={loadingPdf}
          className="w-full mt-3 py-2 rounded-lg text-xs font-semibold"
          style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>
          {loadingPdf ? 'Menyiapkan...' : '⬇ Unduh Laporan Latihan (PDF)'}
        </button>
      </div>

      {/* Gamifikasi */}
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>⭐ Level {gamifikasi.level}</p>
          <p className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{gamifikasi.xp} XP</p>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <p className="text-lg font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{aktivitas.totalSesi}</p>
            <p className="text-[9px] uppercase" style={{ color: 'var(--text-muted)' }}>Sesi</p>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <p className="text-lg font-bold font-mono" style={{ color: 'var(--accent)' }}>{aktivitas.rataRata}</p>
            <p className="text-[9px] uppercase" style={{ color: 'var(--text-muted)' }}>Rata-rata</p>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <p className="text-lg font-bold font-mono" style={{ color: gamifikasi.streak > 0 ? '#FBBF24' : 'var(--text-muted)' }}>
              {gamifikasi.streak > 0 ? `${gamifikasi.streak}🔥` : '—'}
            </p>
            <p className="text-[9px] uppercase" style={{ color: 'var(--text-muted)' }}>Streak</p>
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
          Lencana ({badgeTercapai.length}/{gamifikasi.badges.length})
        </p>
        <div className="flex flex-wrap gap-1.5">
          {gamifikasi.badges.map(b => (
            <span key={b.id} title={b.label}
              className="text-[10px] px-2 py-1 rounded-lg"
              style={{ backgroundColor: b.tercapai ? 'rgba(192,160,98,0.15)' : 'var(--bg-card3)', color: b.tercapai ? 'var(--accent)' : 'var(--text-faint)', border: `1px solid ${b.tercapai ? 'var(--accent)' : 'var(--border)'}` }}>
              {b.icon} {b.label}
            </span>
          ))}
        </div>
      </div>

      {/* Peringatan */}
      {aktivitas.peringatan.length > 0 && (
        <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)' }}>
          <p className="text-xs font-bold mb-2" style={{ color: '#f87171' }}>⚠️ Perlu Perhatian</p>
          <ul className="space-y-1">
            {aktivitas.peringatan.map((p, i) => (
              <li key={i} className="text-xs" style={{ color: 'var(--text-muted)' }}>• {p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Radar kekuatan/kelemahan */}
      {modulDenganData.length >= 3 && (
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>
            Peta Kekuatan Antar Modul
          </p>
          <GrafikRadar perModul={aktivitas.perModul} />
        </div>
      )}

      {/* Grafik per modul */}
      {modulDenganData.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>
            Grafik Perkembangan per Modul
          </p>
          <div className="space-y-2">
            {modulDenganData.map(m => (
              <GrafikModulPenuh key={m} modulId={m} data={aktivitas.perModul[m]} />
            ))}
          </div>
        </div>
      )}

      {/* Riwayat Tryout */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>
          Riwayat Tryout ({tryout.length})
        </p>
        {tryout.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Belum pernah ikut tryout resmi.</p>
        ) : (
          <div className="space-y-1.5">
            {tryout.map(t => (
              <div key={t.id} className="rounded-lg p-3 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{t.tryout_judul || `Tryout #${t.tryout_id}`}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{formatTgl(t.selesai_at)}</p>
                </div>
                <p className="text-base font-bold font-mono" style={{ color: 'var(--accent)' }}>{t.skor_akumulasi}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Riwayat Pembayaran */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>
          Riwayat Pembayaran ({pembayaran.length})
        </p>
        {pembayaran.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Belum ada transaksi.</p>
        ) : (
          <div className="space-y-1.5">
            {pembayaran.map(p => {
              const warna = p.status === 'success' ? 'var(--success)' : p.status === 'pending' ? '#facc15' : '#f87171';
              return (
                <div key={p.id} className="rounded-lg p-3 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{p.tryout_judul || `Tryout #${p.tryout_id}`}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{formatTgl(p.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{formatRupiah(p.jumlah)}</p>
                    <span className="text-[9px] font-semibold" style={{ color: warna }}>{p.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
